import { Menu, MenuItem, Tab, Tabs, Typography } from '@material-ui/core'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Edit, ListOutlined, LoupeOutlined, Send } from '@material-ui/icons'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumBloqueoCapacidad, EnumDispatchType, EnumOperationState } from '../../api'
import { NotificationMessageIcons } from '../../components'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import { changeActionIsLoading } from '../../helpers/dataTableActionIconHelper'
import { normalizeFilename } from '../../helpers/fileDownloadHelper'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { useHttpError } from '../../hooks/useHttpError'
import { useReferredState } from '../../hooks/useReferredState'
import useRetries from '../../hooks/useRetries'
import { DatePeriod, GlobalNotification } from '../../model'
import { ActionIcons, NonAuthRoutes } from '../../model/enums'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { Pages } from '../../model/RBAC/constants'
import { ServiceResponse } from '../../model/serviceResponse'
import {
  getCustomerDomain,
  RootStoreModel,
  storeActiveTabForCurrentPage,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../redux'
import { checkOperationsState } from '../../services/operations.service'
import {
  canSendMassive,
  emptyArrayColumns,
  fetchCapacities,
  fetchOperationAllocations,
  fillRowsArrayColumn,
  filterOperationAllocations,
  groupDataTableSubRowActionsColumn,
  groupDataTableSubRowSecondaryActionsColumn,
  sendOperation,
  sendOperations,
  updateLockState
} from './allocation.service'
import {
  AllocationFilterViewModel,
  AllocationModel,
  OperationAllocationsViewModel
} from './allocations.model'

export interface AllocationPageProps {}

export const AllocationsPage: React.FC<AllocationPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'allocations', 'enums', 'confirmDialogs'])
  let history = useHistory()

  const { dataTableFilter, dataTableSearch, dateFilter, customFilter, activeTab } = usePageState()

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )
  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const { info } = useNotify()

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)
  const [currentTab, setCurrentTab] = React.useState(0)
  const [allocationFilters, setAllocationFilters] = React.useState<
    AllocationFilterViewModel[] | null
  >(null)
  const [operationAllocations, operationAllocationsRef, setOperationAllocations] = useReferredState<
    OperationAllocationsViewModel[] | null
  >(null)

  const [filteredOperationAllocations, setFilteredOperationAllocations] = React.useState<
    OperationAllocationsViewModel[] | undefined
  >(undefined)

  const [activeAllocationFilter, setActiveAllocationFilter] = React.useState<
    AllocationFilterViewModel | undefined
  >(undefined)

  const [editOperationAllocation, setEditOperationAllocation] =
    React.useState<OperationAllocationsViewModel | null>(null)

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter?.initDate
      ? moment(dateFilter.initDate).toDate()
      : moment().startOf('month').toDate(),
    end: dateFilter?.endDate
      ? moment(dateFilter.endDate).toDate()
      : moment().endOf('month').toDate()
  })

  const [editMenuAnchorEl, setEditMenuAnchorEl] = React.useState(null)

  const [editMode, setEditMode] = React.useState<EditMode | undefined>(undefined)
  const [isFetching, setIsFetching] = React.useState<boolean>(true)

  const [customFilterApplied, setCustomFilterApplied] = React.useState<any>({
    startDate: customFilter ? customFilter.startDate : moment().startOf('month').toDate(),
    endDate: customFilter ? customFilter.endDate : moment().endOf('month').toDate()
  })

  //#region Enums

  enum EditMode {
    NOMINATION,
    ALLOCATION
  }

  //#endregion

  //#region Effects

  const { showError, clearError, errorProps } = useHttpError()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async (pendingOperations: OperationAllocationsViewModel[]) => {
      pendingOperations = await monitorOperations(pendingOperations)

      if (pendingOperations.length === 0) {
        isRetriesTimerActive && stopRetriesTimer()
        setGlobalNotification(undefined)
        info(t('allocations:notifications.success.retriesTSOFinish'))
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: count => {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('allocations:globalNotifications.retries.tso.messages.onRetry'),
          actions: [
            {
              label: t('allocations:globalNotifications.retries.tso.actions.cancel'),
              onClick: () => {
                isRetriesTimerActive && stopRetriesTimer()
                setGlobalNotification({
                  message: t('allocations:globalNotifications.retries.tso.messages.onStop'),
                  actions: [
                    {
                      label: t('allocations:globalNotifications.retries.tso.actions.refresh'),
                      onClick: async () => {
                        setOperationAllocations(
                          await getOperations(
                            customFilterApplied?.startDate
                              ? customFilterApplied?.startDate!
                              : dateRange.start,
                            customFilterApplied?.endDate
                              ? customFilterApplied?.endDate!
                              : dateRange.end
                          )
                        )
                      }
                    },
                    {
                      label: t('allocations:globalNotifications.retries.tso.actions.close'),
                      onClick: () => {
                        setGlobalNotification(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.INFO,
          message: t('allocations:globalNotifications.retries.tso.messages.onFinish'),
          actions: [
            {
              label: t('allocations:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                setOperationAllocations(
                  await getOperations(
                    customFilterApplied?.startDate
                      ? customFilterApplied?.startDate!
                      : dateRange.start,
                    customFilterApplied?.endDate ? customFilterApplied?.endDate! : dateRange.end
                  )
                )
              }
            },
            {
              label: t('allocations:globalNotifications.retries.tso.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: error => {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('allocations:globalNotifications.retries.tso.messages.onError'),
          actions: [
            {
              label: t('allocations:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                setOperationAllocations(
                  await getOperations(
                    customFilterApplied?.startDate
                      ? customFilterApplied?.startDate!
                      : dateRange.start,
                    customFilterApplied?.endDate ? customFilterApplied?.endDate! : dateRange.end
                  )
                )
              }
            }
          ]
        })
      }
    }
  )

  useEffect(() => {
    loadPage()
    setActiveAllocationFilter(undefined)
    setFilteredOperationAllocations(undefined)
    setOperationAllocations(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, activeBalanceZone, dateRange])
  useEffect(() => {
    const getOperationAllocations = async () => {
      setOperationAllocations(
        await getOperations(
          customFilterApplied?.startDate ? customFilterApplied?.startDate! : dateRange.start,
          customFilterApplied?.endDate ? customFilterApplied?.endDate! : dateRange.end
        )
      )
    }
    if (allocationFilters) {
      if (activeTab !== undefined) {
        setActiveAllocationFilter(allocationFilters[activeTab])
        setCurrentTab(activeTab)
      } else {
        setActiveAllocationFilter(allocationFilters[0])
        setCurrentTab(0)
      }
      getOperationAllocations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationFilters])

  useEffect(() => {
    Array.isArray(operationAllocations) &&
      setFilteredOperationAllocations(
        filterOperationAllocations(operationAllocations, activeAllocationFilter?.ids)
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAllocationFilter])

  useEffect(() => {
    if (Array.isArray(operationAllocations) && operationAllocations !== undefined) {
      setFilteredOperationAllocations(
        filterOperationAllocations(operationAllocations, activeAllocationFilter?.ids)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationAllocations])

  useEffect(() => {
    if (!editOperationAllocation || editMode === undefined) return
    if (editMode === EditMode.ALLOCATION) {
      history.push(NonAuthRoutes.allocationsEdit, {
        id: editOperationAllocation.id,
        dispatchType: EnumDispatchType.Reparto02G
      })
      return
    }
    if (editMode === EditMode.NOMINATION) {
      history.push(NonAuthRoutes.allocationsEdit, {
        id: editOperationAllocation.id,
        dispatchType: EnumDispatchType.NominacionConRepartos
      })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, editOperationAllocation])

  //#endregion

  //#region Handlers

  const onSelectDateHandler = (startDate: Date, endDate: Date): void => {
    setOperationAllocations(null)
    setFilteredOperationAllocations(undefined)
    setDateRange({ ...dateRange, start: startDate, end: endDate })
  }

  const onTabChangeHandler = (event: React.ChangeEvent<{}>, tab: number) => {
    if (currentTab !== tab) {
      setCurrentTab(tab)
      storeActiveTabForCurrentPage(tab)
    }
  }

  const onTabClickHandler = (allocationFilter: AllocationFilterViewModel) => {
    setActiveAllocationFilter(allocationFilter)
  }

  const onEditMenuCloseHandler = () => {
    setEditMenuAnchorEl(null)
  }

  const onEditMenuClickHandler = async (editMode: EditMode) => {
    setEditMode(editMode)
    onEditMenuCloseHandler()
  }

  const onEditActionHandler = (row: OperationAllocationsViewModel, event: any) => {
    setEditOperationAllocation(row)

    if (moment(row.operationDate).isBefore(moment(), 'day')) {
      setEditMode(EditMode.ALLOCATION)
    } else {
      setEditMenuAnchorEl(event.currentTarget)
    }
  }

  const onLockActionHandler = async (item: AllocationModel) => {
    getConfirmation(ConfirmationDialogs.LOCK).then(async () => {
      await changeProductCapacityLockState(
        item,
        EnumBloqueoCapacidad.BloqueadoUsuario,
        ActionIcons.UNLOCK
      )
      closeConfirmation()
    })
  }

  const onUnlockActionHandler = async (item: AllocationModel) => {
    getConfirmation(ConfirmationDialogs.UNLOCK).then(async () => {
      await changeProductCapacityLockState(item, EnumBloqueoCapacidad.Libre, ActionIcons.LOCK)
      closeConfirmation()
    })
  }

  const onSendActionHandler = async (item: OperationAllocationsViewModel) => {
    getConfirmation(ConfirmationDialogs.SEND).then(async () => {
      await sendOperation(item).then(async success => {
        if (success) {
          info(t('allocations:notifications.success.sendOperation'))
        }
      })
      closeConfirmation()
    })
  }

  const onSendMassiveActionHandler = async (items: OperationAllocationsViewModel[]) => {
    if (canSendMassive(items)) {
      getConfirmation(ConfirmationDialogs.SEND_MASSIVE).then(async () => {
        await sendOperations(items).then(async success => {
          if (success) {
            info(t('allocations:notifications.success.sendOperations'))
            startRetriesTimer(items)
          }
        })
        closeConfirmation()
      })
    } else {
      info(t('allocations:notifications.error.notValidToBeSent'))
    }
  }

  //#endregion

  //#region Functions

  const loadPage = async () => {
    setAllocationFilters(await getCapacities(dateRange.start, dateRange.end))
  }

  const monitorOperations = async (pendingOperations: OperationAllocationsViewModel[]) => {
    let currentOperations = operationAllocations

    return await checkOperationsState(
      pendingOperations.map(op => op.id),
      [EnumOperationState.Enviada, EnumOperationState.Rechazado, EnumOperationState.EnviadoKO]
    ).then(changedOperations => {
      changedOperations.forEach(changedOperation => {
        if (currentOperations) {
          const index = currentOperations.findIndex(x => x.id === changedOperation.id)

          currentOperations = [
            ...currentOperations!.slice(0, index),
            completeDataTableRow({ ...changedOperation }),
            ...currentOperations!.slice(index + 1)
          ]
          setOperationAllocations(currentOperations)
        }

        const pendingIndex = pendingOperations.findIndex(x => x.id === changedOperation.id)

        pendingOperations = [
          ...pendingOperations!.slice(0, pendingIndex),
          ...pendingOperations!.slice(pendingIndex + 1)
        ]
      })

      return pendingOperations
    })
  }

  const updateDateTableRowProductCapacityData = (
    row: AllocationModel,
    state: EnumBloqueoCapacidad
  ) => {
    if (operationAllocationsRef.current) {
      setOperationAllocations(
        operationAllocationsRef.current.map(item => {
          const index = item.allocations.findIndex(
            x => x.productCapacityId === row?.productCapacityId
          )
          if (index !== -1) {
            return completeDataTableRow({
              ...item,
              allocations: [
                ...item.allocations.slice(0, index),
                { ...row, locked: state },
                ...item.allocations.slice(index + 1)
              ]
            })
          } else {
            return { ...item }
          }
        })
      )
    }
  }

  const updateDataTableRowData = (row: AllocationModel) => {
    if (operationAllocationsRef.current) {
      setOperationAllocations(
        operationAllocationsRef.current.map(item => {
          const index = item.allocations.findIndex(x => x.id === row?.id)
          if (index !== -1) {
            return completeDataTableRow({
              ...item,
              allocations: [
                ...item.allocations.slice(0, index),
                { ...row },
                ...item.allocations.slice(index + 1)
              ]
            })
          } else {
            return { ...item }
          }
        })
      )
    }
  }

  const completeDataTableRow = (
    item: OperationAllocationsViewModel
  ): OperationAllocationsViewModel => {
    return fillRowsArrayColumn(
      emptyArrayColumns({
        ...item,
        actionsSubRows: groupDataTableSubRowActionsColumn(
          item.allocations,
          new Map<ActionIcons, Function>([
            [ActionIcons.LOCK, onUnlockActionHandler],
            [ActionIcons.UNLOCK, onLockActionHandler]
          ])
        ),
        secondaryActionsSubRows: groupDataTableSubRowSecondaryActionsColumn(
          item.allocations,
          new Map<ActionIcons, Function>([])
        )
      })
    )
  }

  function a11yProps(index: any) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    }
  }

  const changeProductCapacityLockState = async (
    allocation: AllocationModel,
    lock: EnumBloqueoCapacidad,
    icon: ActionIcons
  ) => {
    if (allocation.locked !== undefined && allocation.productCapacityId !== undefined) {
      updateDataTableRowData({
        ...allocation,
        actionsConfiguration: changeActionIsLoading(allocation.actionsConfiguration, icon, true)
      })

      await updateLockState(allocation.productCapacityId, lock).then(success => {
        if (success) {
          updateDateTableRowProductCapacityData(
            {
              ...allocation,
              actionsConfiguration: changeActionIsLoading(
                allocation.actionsConfiguration,
                icon,
                false
              )
            },
            lock
          )

          info(t('allocations:notifications.success.updateLockState'))
        }
      })
    }
  }

  const getCapacities = async (
    startDate: Date,
    endDate: Date
  ): Promise<AllocationFilterViewModel[] | null> => {
    setIsFetching(true)
    return await fetchCapacities(startDate, endDate).then((response: ServiceResponse<any>) => {
      setIsFetching(false)
      if (response.success) {
        clearError()
        return response.data
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              setAllocationFilters(await getCapacities(dateRange.start, dateRange.end))
            }
          })
        return []
      }
    })
  }

  const getOperations = async (
    startDate: Date,
    endDate: Date
  ): Promise<OperationAllocationsViewModel[] | null> => {
    if (!allocationFilters || allocationFilters.length === 0) {
      return []
    }
    return await fetchOperationAllocations(startDate, endDate).then(
      (response: ServiceResponse<any>) => {
        setIsFetching(false)
        if (response.success) {
          clearError()
          return response.data.map(item => {
            return completeDataTableRow(item)
          })
        } else {
          setAllocationFilters(null)
          response.error &&
            response.error &&
            showError({
              errorCode: response.error.code,
              onRefresh: async () => {
                setAllocationFilters(await getCapacities(dateRange.start, dateRange.end))
              }
            })
          return []
        }
      }
    )
  }

  //#endregion

  //#region DataTable configuration

  const options: DataTableOptions = React.useMemo(() => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: 1,
      print: false,
      filename: normalizeFilename(t('allocations:title')),
      download: true,
      selectableRows: true,
      customInsertEditModal: true,
      canInsert: false,
      canEdit: false,
      canDelete: false,
      analytics: {
        customer: getCustomerDomain(),
        view: t('allocations:dataTable.analytics')
      },
      rowCustomActions: [
        {
          userPermission: UserPermission.SEND,
          icon: <Send fontSize="small" />,
          visibleIf: 'canSend',
          tooltip: t('allocations:dataTable.customActions.sendTSO'),
          onClick: onSendActionHandler
        },
        {
          userPermission: UserPermission.EDIT,
          icon: <Edit fontSize="small" />,
          tooltip: t('allocations:dataTable.customActions.edit'),
          onClick: onEditActionHandler
        }
      ],
      selectToolbarCustomActions: [
        {
          userPermission: UserPermission.SEND,
          icon: <Send fontSize="small" />,
          tooltip: t('allocations:dataTable.customActions.sendTSO'),
          onClick: onSendMassiveActionHandler
        }
      ],
      onFilterChange: (filter: ColumnFilter[]) => {
        storeDataTableFilterForCurrentPage(filter)
      },
      onSearchChange: (search: string) => {
        storeDataTableSearchForCurrentPage(search)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationAllocations])

  const editOptions = React.useMemo(() => {
    return [
      {
        label: t('allocations:dataTable.customActions.editNomination'),
        icon: <LoupeOutlined />,
        mode: EditMode.NOMINATION
      },
      {
        label: t('allocations:dataTable.customActions.editAllocations'),
        icon: <ListOutlined />,
        mode: EditMode.ALLOCATION
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: DataTableColumn[] = React.useMemo(() => {
    return [
      {
        name: 'operationDate',
        type: DataType.DATE,
        label: t('allocations:dataTable.columns.operationDate')
      },
      {
        name: 'quantity',
        type: DataType.NUMBER,
        label: t('allocations:dataTable.columns.quantity'),
        decimalScale: DecimalScale.ENERGY
      },
      {
        name: 'actionsSubRows',
        label: ' ',
        type: DataType.ARRAY,
        columnWidth: 25,
        download: false
      },
      {
        name: 'productCapacityBreakdownSubRows',
        label: t('allocations:dataTable.columns.productCapacity'),
        type: DataType.ARRAY,
        columnWidth: 50
      },
      {
        name: 'secondaryActionsSubRows',
        label: ' ',
        type: DataType.ARRAY,
        columnWidth: 30,
        download: false
      },
      {
        name: 'capacitySubRows',
        label: t('allocations:dataTable.columns.capacity'),
        type: DataType.ARRAY,
        hideForResponsive: true
      },
      {
        name: 'allocatedQuantityBreakdownSubRows',
        label: t('allocations:dataTable.columns.allocatedQuantity'),
        type: DataType.ARRAY
      },
      {
        name: 'lastSendBreakdownSubRows',
        label: t('allocations:dataTable.columns.lastSend'),
        type: DataType.ARRAY,
        hideForResponsive: true
      },
      {
        name: 'TSOInformationBreakdownSubRows',
        label: t('allocations:dataTable.columns.TSOInformation'),
        type: DataType.ARRAY,
        hideForResponsive: true
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const countAllocations = async (startDate: Date, endDate: Date): Promise<number | undefined> => {
    const operations = await getOperations(startDate, endDate)
    return operations ? operations.length : undefined
  }

  const onApplyCustomFilter = async (filterToApply: any) => {
    setCustomFilterApplied(filterToApply)
    onSelectDateHandler(filterToApply.startDate, filterToApply.endDate)
    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const totalCount = await countAllocations(filterToApply.startDate, filterToApply.endDate)
    if (totalCount !== undefined && totalCount < 1000) {
      return true
    }
    return false
  }

  //#endregion

  return (
    <>
      <Page notification={globalNotification}>
        <PageHeader title={t('allocations:title')}>
          <CustomFilter
            onApplyFilter={onApplyCustomFilter}
            onPrevalidateFilter={onPrevalidateCustomFilter}
            appliedFilter={customFilterApplied}
            enabled={!isFetching}
          ></CustomFilter>
        </PageHeader>
        <Tabs
          value={currentTab}
          onChange={onTabChangeHandler}
          aria-label="scrollable auto tabs"
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {allocationFilters &&
            allocationFilters.map((capacity, index) => {
              return (
                <Tab
                  key={index}
                  label={capacity.description}
                  {...a11yProps(index)}
                  onClick={() => {
                    onTabClickHandler(capacity)
                  }}
                />
              )
            })}
        </Tabs>
        <DataTable
          data={filteredOperationAllocations}
          columns={columns}
          options={{
            ...options,
            analytics: {
              customer: getCustomerDomain(),
              view: t('allocations:dataTable.analytics')
            }
          }}
          info={DataTableErrorHandler(errorProps)}
          userPermissions={getUserPermissions(Pages.Allocations)}
          appliedFilter={dataTableFilter}
          appliedSearch={dataTableSearch}
        />

        <Menu
          keepMounted={false}
          elevation={2}
          anchorEl={editMenuAnchorEl}
          open={Boolean(editMenuAnchorEl)}
          onClose={onEditMenuCloseHandler}
        >
          {editOptions.map((editOption, index) => (
            <MenuItem dense key={index} onClick={() => onEditMenuClickHandler(editOption.mode)}>
              <Typography variant="inherit">{editOption.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Page>
    </>
  )
}
